// Wording and dismissal rules of the overdue-payment banner. Kept apart from
// the component so the platform console's live preview and the real banner
// say exactly the same thing.
import type { TFunction } from 'i18next';
import type { TenantBillingNotice } from '../../api/billingNotice';

/** Where a customer writes about the payment. Change it here only: the
 *  banner, the preview and their tests all read this constant. */
export const BILLING_CONTACT_EMAIL = 'billing@maic.ai';

/** "15 de noviembre de 2026" / "November 15, 2026". The deadline is a
 *  calendar date, so it is formatted in UTC: no timezone can move it a day. */
export function formatDeadline(iso: string, lang: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(lang.startsWith('en') ? 'en-US' : 'es', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  }).format(d);
}

export function noticeTitle(t: TFunction, notice: Pick<TenantBillingNotice, 'level'>): string {
  return notice.level === 'final'
    ? t('shell.billingNotice.finalTitle', 'Aviso final de pago')
    : t('shell.billingNotice.reminderTitle', 'Pago pendiente');
}

export function noticeMessage(t: TFunction, notice: TenantBillingNotice, lang: string): string {
  const vars = {
    company: notice.tenant_name || t('shell.billingNotice.yourCompany', 'tu empresa'),
    date: notice.deadline ? formatDeadline(notice.deadline, lang) : '',
    email: BILLING_CONTACT_EMAIL,
  };
  if (notice.level === 'final') {
    return notice.deadline
      ? t('shell.billingNotice.finalWithDate', 'El servicio de {{company}} se suspenderá el {{date}} por un pago pendiente. Por favor regulariza el pago o escríbenos a {{email}} para evitar la suspensión.', vars)
      : t('shell.billingNotice.final', 'El servicio de {{company}} se suspenderá próximamente por un pago pendiente. Por favor regulariza el pago o escríbenos a {{email}} para evitar la suspensión.', vars);
  }
  return notice.deadline
    ? t('shell.billingNotice.reminderWithDate', 'La cuenta de {{company}} tiene un pago pendiente. Para evitar interrupciones en el servicio, por favor regulariza el pago antes del {{date}}. Si ya lo realizaste, ignora este mensaje o escríbenos a {{email}}.', vars)
    : t('shell.billingNotice.reminder', 'La cuenta de {{company}} tiene un pago pendiente. Para evitar interrupciones en el servicio, por favor regulariza el pago. Si ya lo realizaste, ignora este mensaje o escríbenos a {{email}}.', vars);
}

// ── Dismissal ────────────────────────────────────────────────────────────────
// A reminder can be hidden until the end of the day, in this browser session.
// It comes back the next day, in a new session, or as soon as the operator
// changes the notice. A final notice cannot be hidden.

export const DISMISS_KEY = 'nexus.billing_notice.dismissed';

/** Local calendar day, YYYY-MM-DD. */
export function localDay(now: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

function signature(tenantId: string, notice: TenantBillingNotice): string {
  return [tenantId, notice.level, notice.deadline ?? '', notice.set_at ?? ''].join('|');
}

export function isDismissed(tenantId: string, notice: TenantBillingNotice, now: Date): boolean {
  if (notice.level === 'final') return false;
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const saved = JSON.parse(raw) as { sig?: string; day?: string };
    return saved.sig === signature(tenantId, notice) && saved.day === localDay(now);
  } catch {
    return false;
  }
}

export function dismiss(tenantId: string, notice: TenantBillingNotice, now: Date): void {
  if (notice.level === 'final') return;
  try {
    sessionStorage.setItem(DISMISS_KEY, JSON.stringify({ sig: signature(tenantId, notice), day: localDay(now) }));
  } catch { /* storage blocked: hidden for this page load only */ }
}
